import { FormattedMessage } from 'react-intl';

import type { FilterWidget } from '@sorare/core/src/lib/filters';
import { FILTERS } from '@sorare/core/src/lib/filters';
import { cardAttributes, glossary } from '@sorare/core/src/lib/glossary';

import { widget as ShowDetails } from '#src/components/TokenPreview/ShowDetails/index.tsx';
import { RangeSlider as ConnectedRangeSlider } from '#src/search/RangeSlider/index.tsx';
import { makeRangeFilter } from '#src/searchCards/RangeSlider/index.tsx';
import {
  RefineActiveClub,
  RefineClub,
} from '#src/searchCards/RefineClubs/index.tsx';
import {
  AcceptedCurrencyGroup,
  BidToEarnFilter,
  DailyFilter,
  NotInLineupFilter,
  PriceGroup,
  PromotionalEventFilter,
  PromotionalEventFilterForInstantBuy,
  RarityGroup,
  RefineAppearances,
  RefineCardEdition,
  RefineCardLevel,
  RefineCardPlayerAge,
  RefineCreditEligible,
  RefineInSeason,
  RefineNationality,
  RefineOnSale,
  RefinePlayer,
  RefinePosition,
  RefineSerialNumber,
  RefineSingleYearSeason,
  filterSeparator,
} from '#src/searchCards/index.ts';
import { WatchlistGroup } from '#src/searchCards/refineGroups.tsx';

import { RefineTenGameAverageScore } from '../nba/cardFilters.tsx';
import { widget as BaseballCustomDecksFilter } from './BaseballCustomDecksFilter/index.tsx';
import { RefineMLBPosition } from './RefineMLBPosition/index.tsx';
import { widget as RefineProbablePitchers } from './RefineProbablePitchers/index.tsx';

const MAX_TEAM_LIMIT = 30;

const RefineSeasonAverageScore = makeRangeFilter(({ visible }) => {
  return (
    <ConnectedRangeSlider
      attribute={FILTERS.mlbSeasonAverageScore.attribute}
      visible={visible}
    />
  );
}, FILTERS.mlbSeasonAverageScore);

const InSeasonFilter = {
  slug: 'inSeason',
  title: null,
  filter: RefineInSeason,
  filters: [],
};

const CardGroup = {
  slug: 'card',
  title: <FormattedMessage {...glossary.card} />,
  filters: [
    RefineMLBPosition,
    RefineSingleYearSeason,
    RefineClub(MAX_TEAM_LIMIT),
  ],
};

export const MatchInfoGroup = (
  withSeasonScore: boolean,
  props?: {
    withoutProbablePitchers?: boolean;
  }
) => ({
  slug: 'match-info',
  title: <FormattedMessage {...glossary.matchInfo} />,
  filters: [
    DailyFilter,
    withSeasonScore ? RefineSeasonAverageScore : RefineTenGameAverageScore,
    RefineAppearances(),
    ...((props?.withoutProbablePitchers && []) || [RefineProbablePitchers]),
  ],
});

export const PlayerGroup = (props?: {
  withoutActiveClub?: boolean;
  withClub?: boolean;
}) => ({
  slug: 'player',
  title: <FormattedMessage {...cardAttributes.player} />,
  filters: [
    RefinePosition(),
    RefineCardPlayerAge,
    RefineNationality,
    RefinePlayer,
    ...((props?.withoutActiveClub && []) || [RefineActiveClub(MAX_TEAM_LIMIT)]),
    ...((props?.withClub && [RefineClub(MAX_TEAM_LIMIT)]) || []),
  ],
});

const CollectibleGroup = (props?: { withoutCardLevel?: boolean }) => ({
  slug: 'collectibles',
  title: <FormattedMessage {...cardAttributes.collectibles} />,
  filters: [
    RefineSerialNumber,
    ...((props?.withoutCardLevel && []) || [RefineCardLevel]),
    RefineCardEdition,
  ],
});

const ToggleGroup = (filters: FilterWidget[]) => ({
  slug: 'toggle',
  title: null,
  filters: [
    ...(filters.flatMap((filter, i) => [
      ...((i && [filterSeparator]) || []),
      filter,
    ]) || []),
  ],
});

export const primaryMarketFilters = (withSeasonScore: boolean) => [
  RarityGroup,
  {
    slug: 'toggle',
    title: null,
    filters: [PromotionalEventFilter],
  },
  PriceGroup,
  ToggleGroup([BidToEarnFilter()]),
  WatchlistGroup,
  CardGroup,
  MatchInfoGroup(withSeasonScore),
  PlayerGroup(),
  CollectibleGroup({ withoutCardLevel: true }),
];

export const instantBuyMarketFilters = (withSeasonScore: boolean) => [
  RarityGroup,
  {
    slug: 'toggle',
    title: null,
    filters: [PromotionalEventFilterForInstantBuy],
  },
  PriceGroup,
  WatchlistGroup,
  CardGroup,
  MatchInfoGroup(withSeasonScore, { withoutProbablePitchers: true }),
  PlayerGroup(),
  CollectibleGroup({ withoutCardLevel: true }),
];

export const secondaryMarketFilters = (withSeasonScore: boolean) =>
  [
    InSeasonFilter,
    RarityGroup,
    PriceGroup,
    WatchlistGroup,
    CardGroup,
    MatchInfoGroup(withSeasonScore),
    PlayerGroup(),
    CollectibleGroup(),
    AcceptedCurrencyGroup,
  ].filter(Boolean);

export const secondaryMarketStackedFilters = [
  PriceGroup,
  CardGroup,
  CollectibleGroup(),
  AcceptedCurrencyGroup,
].filter(Boolean);

export const playerPageFilters = (withSeasonScore: boolean) => [
  InSeasonFilter,
  ToggleGroup([RefineOnSale(), RefineCreditEligible()]),
  RarityGroup,
  PriceGroup,
  CardGroup,
  MatchInfoGroup(withSeasonScore, { withoutProbablePitchers: true }),
  CollectibleGroup(),
  AcceptedCurrencyGroup,
];

export const clubPageFilters = (withSeasonScore: boolean) => [
  InSeasonFilter,
  ToggleGroup([RefineOnSale(), RefineCreditEligible()]),
  RarityGroup,
  PriceGroup,
  WatchlistGroup,
  CardGroup,
  MatchInfoGroup(withSeasonScore),
  PlayerGroup({ withoutActiveClub: false }),
  CollectibleGroup(),
  AcceptedCurrencyGroup,
];

export const myClubFilters = ({
  withSeasonScore,
  showDecksFilter,
}: {
  withSeasonScore: boolean;
  showDecksFilter: boolean;
}) =>
  [
    { slug: 'show-details', filters: [ShowDetails] },
    InSeasonFilter,
    ToggleGroup([RefineOnSale(), NotInLineupFilter]),
    ...((showDecksFilter && [
      { slug: 'decks', filters: [BaseballCustomDecksFilter] },
    ]) ||
      []),
    WatchlistGroup,
    CardGroup,
    MatchInfoGroup(withSeasonScore),
    PlayerGroup(),
    CollectibleGroup(),
    AcceptedCurrencyGroup,
  ].filter(Boolean);

export const tradeFilters = [
  InSeasonFilter,
  RarityGroup,
  { slug: 'not-in-lineup', filters: [NotInLineupFilter] },
  WatchlistGroup,
  {
    slug: 'deck',
    filters: [BaseballCustomDecksFilter],
  },
  CardGroup,
];

export const myVaultFilters = [
  { slug: 'show-details', filters: [ShowDetails] },
  InSeasonFilter,
  CardGroup,
  PlayerGroup(),
  CollectibleGroup(),
];
